import { DatePicker, Space } from 'antd';
import { Dayjs } from 'dayjs';
import styles from './custom-range-picker.module.scss';
import clsx from 'clsx';
import { RangePickerProps } from 'antd/es/date-picker';
import Icon, { CloseOutlined } from '@ant-design/icons';
import InfoIcon from '@/assets/icons/info-circle-icon.svg?react';

const { RangePicker } = DatePicker;

interface CustomRangePickerProps extends Omit<RangePickerProps, 'onChange'> {
  dates: [Dayjs | null, Dayjs | null] | null;
  setDates: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  onChange: (filterValue: { time_range: { start_time: string; end_time: string } }) => void;
  onClear: () => void;
  className?: string;
}

export const CustomRangePicker: React.FC<CustomRangePickerProps> = ({
  dates,
  setDates,
  onChange,
  onClear,
  className,
  ...props
}) => {
  const handleRangeChange = (
    dates: [Dayjs | null, Dayjs | null],
    dateStrings: [string, string]
  ) => {
    if (!dates || !dates[0] || !dates[1]) return;

    const diffDays = dates[1].diff(dates[0], 'day');

    if (diffDays > 90) {
      setDates(null);
      onChange({ time_range: { start_time: '', end_time: '' } });
    } else {
      onChange({
        time_range: {
          start_time: dateStrings[0],
          end_time: dateStrings[1],
        },
      });
      setDates(dates);
    }
  };

  const handleClear = () => {
    setDates(null);
    onClear();
  };

  const disabledDate = (current: Dayjs | null): boolean => {
    if (!current) return false;

    if (current.isAfter(new Date(), 'day')) return true;
    if (current.isBefore('2024-01-01', 'day')) return true;

    const startDate = dates?.[0];
    const endDate = dates?.[1];

    if (startDate) {
      const endDateLimit = startDate.add(90, 'day');
      if (current.isAfter(endDateLimit)) {
        return true;
      }
    }

    if (endDate) {
      const startDateLimit = endDate.subtract(90, 'day');
      if (current.isBefore(startDateLimit)) {
        return true;
      }
    }

    return false;
  };

  return (
    <Space direction="vertical" className={styles.RangePickerWrapper}>
      <RangePicker
        {...props}
        inputReadOnly={true}
        allowClear={{
          clearIcon: (
            <button className={styles.customRangePickerReset} onClick={handleClear}>
              <CloseOutlined className={styles.clearIcon} />
            </button>
          ),
        }}
        onCalendarChange={(dates, dateStrings) => {
          setDates(dates);
          if (dates && dates[0] && dates[1]) {
            handleRangeChange(dates, dateStrings);
          }
        }}
        disabledDate={disabledDate}
        className={clsx(styles.customRangePicker, className)}
        value={dates || null}
        renderExtraFooter={() => (
          <div className={styles.infoWrapper}>
            <Icon component={() => <InfoIcon className={styles.infoIcon} />} />
            <p className={styles.infoText}>The maximum allowed date range is 90 days</p>
          </div>
        )}
      />
    </Space>
  );
};
